import {
  DownOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FilterOutlined,
  InboxOutlined,
  LoadingOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Button, Col, Row, Spin, Table, Tag, Tooltip, message } from 'antd';
import {
  createIncidenceRequest,
  deleteIncidenceRequest,
  listMyIncidenceRequests,
  updateIncidenceRequest,
} from 'api/incidence-request.api';
import { getCategories } from 'api/incidence.api';
import { ColumnsType } from 'antd/es/table';
import { PageTitle } from 'components/common/PageTitle/PageTitle';
import { useIsMobile } from 'hooks/useResponsive';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import { useNavigate } from 'react-router';
import { ApiError } from 'types';
import {
  CreateIncidenceRequestPayload,
  IncidenceRequestStatus,
  incidenceRequestApiStatusMap,
} from 'types/incidenceRequests';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IncidenceRequestCreateDrawer } from './components/IncidenceRequestCreateDrawer';
import { IncidenceRequestDeleteConfirmModal } from './components/IncidenceRequestDeleteConfirmModal';
import { IncidenceRequestsFilters } from './components/IncidenceRequestsFilters';
import {
  EmployeeIncidenceRequest,
  formatDisplayDate,
  isEditableRequestStatus,
  mapIncidenceRequestRecord,
  statusLabels,
} from './components/incidenceRequest.shared';
import 'styles/sass/incidence-requests.scss';

type IncidenceRequestsTableState = 'empty' | 'empty_filtered' | 'error';
const PAGE_SIZE = 10;

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const downloadReport = (rows: EmployeeIncidenceRequest[]) => {
  if (!rows.length) {
    message.info('No hay registros para descargar');
    return;
  }

  const header =
    'Folio,Tipo de incidencia,Fecha de solicitud,Fecha inicial,Fecha final,Status,Última actualización';
  const body = rows.map((row) =>
    [
      row.folio,
      `"${row.incidenceType}"`,
      formatDisplayDate(row.requestDate),
      formatDisplayDate(row.startDate),
      formatDisplayDate(row.endDate),
      `"${statusLabels[row.status]}"`,
      formatDisplayDate(row.updatedAt),
    ].join(',')
  );
  const csvContent = [header, ...body].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `mis_solicitudes_incidencias_${dayjs().format('YYYYMMDD')}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const EmployeeIncidenceRequestsPage = () => {
  const navigate = useNavigate();
  const mobileOnly = useIsMobile();
  const [requests, setRequests] = useState<EmployeeIncidenceRequest[]>([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingRequestsError, setLoadingRequestsError] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);
  const [editingRequest, setEditingRequest] =
    useState<EmployeeIncidenceRequest | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [requestPendingDeletion, setRequestPendingDeletion] =
    useState<EmployeeIncidenceRequest | null>(null);
  const [expandedMobileRequestId, setExpandedMobileRequestId] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [incidenceTypeFilter, setIncidenceTypeFilter] = useState<
    string | undefined
  >(undefined);
  const [statusFilter, setStatusFilter] = useState<
    IncidenceRequestStatus | undefined
  >(undefined);
  const [fromDateFilter, setFromDateFilter] = useState<Dayjs | null>(null);
  const [toDateFilter, setToDateFilter] = useState<Dayjs | null>(null);
  const [incidenceTypeOptions, setIncidenceTypeOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const syncRequestReferences = useCallback(
    (nextRequests: EmployeeIncidenceRequest[]) => {
      setRequests(nextRequests);
      setEditingRequest((currentRequest) =>
        currentRequest
          ? (nextRequests.find((request) => request.id === currentRequest.id) ??
            null)
          : null
      );
    },
    []
  );

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    setLoadingRequestsError(false);

    try {
      const response = await listMyIncidenceRequests({
        page: currentPage,
        limit: PAGE_SIZE,
        ...(incidenceTypeFilter
          ? { incidenceCategory: incidenceTypeFilter }
          : {}),
        ...(statusFilter
          ? { status: incidenceRequestApiStatusMap[statusFilter] }
          : {}),
        ...(fromDateFilter
          ? { startDate: fromDateFilter.format('YYYY-MM-DD') }
          : {}),
        ...(toDateFilter ? { endDate: toDateFilter.format('YYYY-MM-DD') } : {}),
      });
      syncRequestReferences(response.data.data.map(mapIncidenceRequestRecord));
      setTotalRequests(response.data.pagination.total);
    } catch {
      syncRequestReferences([]);
      setTotalRequests(0);
      setLoadingRequestsError(true);
    } finally {
      setLoadingRequests(false);
    }
  }, [
    currentPage,
    fromDateFilter,
    incidenceTypeFilter,
    statusFilter,
    syncRequestReferences,
    toDateFilter,
  ]);

  const loadIncidenceTypes = useCallback(async () => {
    try {
      const response = await getCategories();
      const filteredCategories = response.data
        .filter((category) => category.solicitable)
        .sort((firstCategory, secondCategory) =>
          firstCategory.desc.localeCompare(secondCategory.desc)
        )
        .map((category) => ({
          label: `${category.abbr} - ${category.desc}`,
          value: category._id,
        }));
      setIncidenceTypeOptions(filteredCategories);
    } catch (error) {
      setIncidenceTypeOptions([]);
      message.error(
        getErrorMessage(
          error,
          'No fue posible cargar el catálogo de incidencias solicitables.'
        )
      );
    }
  }, []);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    void loadIncidenceTypes();
  }, [loadIncidenceTypes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fromDateFilter, incidenceTypeFilter, statusFilter, toDateFilter]);

  const statusOptions = useMemo(
    () =>
      Object.entries(statusLabels).map(([value, label]) => ({
        label,
        value,
      })),
    []
  );

  const clearFilters = () => {
    setIncidenceTypeFilter(undefined);
    setStatusFilter(undefined);
    setFromDateFilter(null);
    setToDateFilter(null);
  };

  const hasActiveFilters = Boolean(
    incidenceTypeFilter || statusFilter || fromDateFilter || toDateFilter
  );

  const incidenceRequestsTableState =
    useMemo<IncidenceRequestsTableState | null>(() => {
      if (loadingRequests) {
        return null;
      }

      if (loadingRequestsError) {
        return 'error';
      }

      if (requests.length > 0) {
        return null;
      }

      if (totalRequests === 0 && !hasActiveFilters) {
        return 'empty';
      }

      return 'empty_filtered';
    }, [
      hasActiveFilters,
      loadingRequests,
      loadingRequestsError,
      requests.length,
      totalRequests,
    ]);

  const downloadReportDisabled =
    loadingRequests || loadingRequestsError || totalRequests === 0;

  const totalPages = Math.max(1, Math.ceil(totalRequests / PAGE_SIZE));
  const mobilePaginatedRequests = requests;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!mobileOnly) {
      return;
    }
    if (!mobilePaginatedRequests.length) {
      setExpandedMobileRequestId(null);
      return;
    }
    if (
      !expandedMobileRequestId ||
      !mobilePaginatedRequests.some(
        (request) => request.id === expandedMobileRequestId
      )
    ) {
      setExpandedMobileRequestId(mobilePaginatedRequests[0].id);
    }
  }, [expandedMobileRequestId, mobileOnly, mobilePaginatedRequests]);

  const onPaginationChange = (page: number) => {
    setCurrentPage(page);
  };

  const openRequestDrawer = () => {
    setEditingRequest(null);
    setRequestDrawerOpen(true);
  };

  const openRequestDrawerForEdition = (request: EmployeeIncidenceRequest) => {
    setEditingRequest(request);
    setRequestDrawerOpen(true);
  };

  const closeRequestDrawer = () => {
    setRequestDrawerOpen(false);
    setEditingRequest(null);
  };

  const openDetailPage = (request: EmployeeIncidenceRequest) => {
    navigate(`/mis-solicitudes-incidencias/${request.id}`);
  };

  const openDeleteModal = (request: EmployeeIncidenceRequest) => {
    setRequestPendingDeletion(request);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setRequestPendingDeletion(null);
  };

  const persistDraftRequest = async (
    payload: CreateIncidenceRequestPayload,
    requestToEdit?: EmployeeIncidenceRequest | null
  ) => {
    try {
      if (requestToEdit) {
        await updateIncidenceRequest(requestToEdit.id, payload);
      } else {
        await createIncidenceRequest(payload);
      }

      await loadRequests();
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'No fue posible guardar la solicitud.')
      );
    }
  };

  const persistAndSubmitRequest = async (
    payload: CreateIncidenceRequestPayload,
    requestToEdit?: EmployeeIncidenceRequest | null
  ) => {
    try {
      if (requestToEdit) {
        await updateIncidenceRequest(requestToEdit.id, {
          ...payload,
          submit: true,
        });
      } else {
        const createdRequest = await createIncidenceRequest(payload);
        await updateIncidenceRequest(createdRequest.data._id, {
          submit: true,
        });
      }

      await loadRequests();
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'No fue posible enviar la solicitud.')
      );
    }
  };

  const confirmDeleteRequest = async () => {
    if (!requestPendingDeletion) {
      return;
    }

    try {
      await deleteIncidenceRequest(requestPendingDeletion.id);
      await loadRequests();

      setExpandedMobileRequestId((currentId) =>
        currentId === requestPendingDeletion.id ? null : currentId
      );

      message.success(`Se eliminó folio ${requestPendingDeletion.folio}`);
      closeDeleteModal();
    } catch (error) {
      message.error(
        getErrorMessage(error, 'No fue posible eliminar la solicitud.')
      );
    }
  };

  const toggleMobileRequest = (id: string) => {
    setExpandedMobileRequestId((currentId) => (currentId === id ? null : id));
  };

  const handleDownloadReport = async () => {
    try {
      const firstPageResponse = await listMyIncidenceRequests({
        page: 1,
        limit: totalRequests || PAGE_SIZE,
        ...(incidenceTypeFilter
          ? { incidenceCategory: incidenceTypeFilter }
          : {}),
        ...(statusFilter
          ? { status: incidenceRequestApiStatusMap[statusFilter] }
          : {}),
        ...(fromDateFilter
          ? { startDate: fromDateFilter.format('YYYY-MM-DD') }
          : {}),
        ...(toDateFilter ? { endDate: toDateFilter.format('YYYY-MM-DD') } : {}),
      });

      downloadReport(
        firstPageResponse.data.data.map(mapIncidenceRequestRecord)
      );
    } catch (error) {
      message.error(
        getErrorMessage(error, 'No fue posible descargar el reporte.')
      );
    }
  };

  const renderIncidenceRequestsTableState = (mobile = false) => {
    if (!incidenceRequestsTableState) {
      return null;
    }

    const isErrorState = incidenceRequestsTableState === 'error';
    const isEmptyState = incidenceRequestsTableState === 'empty';

    const title = isErrorState
      ? 'Error al cargar la información'
      : isEmptyState
        ? 'No hay información para mostrar'
        : 'No hay resultados para tu búsqueda';

    const description = isErrorState
      ? null
      : isEmptyState
        ? 'Cuando solicites una incidencia, aparecerá aquí.'
        : 'Intenta una nueva búsqueda con otros filtros';

    const actionLabel = isErrorState
      ? 'Reintentar'
      : isEmptyState
        ? 'Solicitar incidencia'
        : 'Limpiar filtros';

    const onActionClick = () => {
      if (isErrorState) {
        void loadRequests();
        return;
      }

      if (isEmptyState) {
        openRequestDrawer();
        return;
      }

      clearFilters();
    };

    return (
      <div
        className={`incidence-requests-empty-state ${mobile ? 'incidence-requests-empty-state--mobile' : ''}`}
        data-cy={`incidence-request-empty-state-${incidenceRequestsTableState}`}
      >
        {isErrorState ? (
          <FileExcelOutlined className="incidence-requests-empty-state-icon incidence-requests-empty-state-icon--error" />
        ) : (
          <InboxOutlined className="incidence-requests-empty-state-icon" />
        )}
        <p className="incidence-requests-empty-state-title">{title}</p>
        {description && (
          <p className="incidence-requests-empty-state-description">
            {description}
          </p>
        )}
        <Button
          type="link"
          className="incidence-requests-empty-state-action"
          onClick={onActionClick}
        >
          {actionLabel}
        </Button>
      </div>
    );
  };

  const columns: ColumnsType<EmployeeIncidenceRequest> = [
    {
      title: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      sorter: (firstRequest, secondRequest) =>
        firstRequest.folio.localeCompare(secondRequest.folio),
      render: (folio: string) => <span className="request-folio">{folio}</span>,
    },
    {
      title: 'Tipo de incidencia',
      dataIndex: 'incidenceType',
      key: 'incidenceType',
      sorter: (firstRequest, secondRequest) =>
        firstRequest.incidenceType.localeCompare(secondRequest.incidenceType),
    },
    {
      title: 'Fecha de solicitud',
      dataIndex: 'requestDate',
      key: 'requestDate',
      sorter: (firstRequest, secondRequest) =>
        dayjs(firstRequest.requestDate).valueOf() -
        dayjs(secondRequest.requestDate).valueOf(),
      render: (date: string) => formatDisplayDate(date),
    },
    {
      title: 'Fecha inicial',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: (firstRequest, secondRequest) =>
        dayjs(firstRequest.startDate).valueOf() -
        dayjs(secondRequest.startDate).valueOf(),
      render: (date: string) => formatDisplayDate(date),
    },
    {
      title: 'Fecha final',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: (firstRequest, secondRequest) =>
        dayjs(firstRequest.endDate).valueOf() -
        dayjs(secondRequest.endDate).valueOf(),
      render: (date: string) => formatDisplayDate(date),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: IncidenceRequestStatus) => (
        <Tag
          className={`incidence-request-status incidence-request-status--${status}`}
        >
          {statusLabels[status]}
        </Tag>
      ),
    },
    {
      title: 'Última actualización',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: (firstRequest, secondRequest) =>
        dayjs(firstRequest.updatedAt).valueOf() -
        dayjs(secondRequest.updatedAt).valueOf(),
      render: (date: string) => formatDisplayDate(date),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, request) => {
        const showEditionActions = isEditableRequestStatus(request.status);

        return (
          <div className="option-buttons">
            <Tooltip title="Ver solicitud">
              <Button
                icon={<EyeOutlined />}
                type="text"
                onClick={() => openDetailPage(request)}
                data-cy={`incidence-request-view-btn-${request.id}`}
              />
            </Tooltip>

            {showEditionActions && (
              <Tooltip title="Editar solicitud">
                <Button
                  icon={<EditOutlined />}
                  type="text"
                  onClick={() => openRequestDrawerForEdition(request)}
                  data-cy={`incidence-request-edit-btn-${request.id}`}
                />
              </Tooltip>
            )}

            {showEditionActions && (
              <Tooltip title="Eliminar solicitud">
                <Button
                  icon={<DeleteOutlined className="delete-icon" />}
                  type="text"
                  onClick={() => openDeleteModal(request)}
                  data-cy={`incidence-request-delete-btn-${request.id}`}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div
      id="employee-incidence-requests-page"
      data-cy="employee-incidence-requests-page"
    >
      <PageTitle>Mis solicitudes de incidencias</PageTitle>
      <Row
        className="employee-incidence-header"
        align="middle"
        justify="space-between"
        gutter={[12, 12]}
      >
        <Col flex="auto">
          <div className="employee-incidence-header-title-wrap">
            <h1 className="title">Mis solicitudes de incidencias</h1>
            {mobileOnly && (
              <Button
                className={`mobile-filter-toggle ${showMobileFilters ? 'active' : ''}`}
                icon={<FilterOutlined />}
                onClick={() => setShowMobileFilters((previous) => !previous)}
                data-cy="incidence-request-mobile-filters-toggle"
              />
            )}
          </div>
        </Col>
        {!mobileOnly && (
          <Col>
            <Button
              type="primary"
              onClick={openRequestDrawer}
              data-cy="incidence-request-open-drawer-btn"
            >
              Solicitar incidencia
            </Button>
          </Col>
        )}
      </Row>

      <IncidenceRequestsFilters
        mobileOnly={mobileOnly}
        showMobileFilters={showMobileFilters}
        incidenceTypeOptions={incidenceTypeOptions}
        statusOptions={statusOptions}
        incidenceTypeFilter={incidenceTypeFilter}
        statusFilter={statusFilter}
        fromDateFilter={fromDateFilter}
        toDateFilter={toDateFilter}
        onIncidenceTypeFilterChange={setIncidenceTypeFilter}
        onStatusFilterChange={(value) =>
          setStatusFilter(value as IncidenceRequestStatus | undefined)
        }
        onFromDateFilterChange={setFromDateFilter}
        onToDateFilterChange={setToDateFilter}
        onClearFilters={clearFilters}
        onDownloadReport={() => void handleDownloadReport()}
        disableDownloadReport={downloadReportDisabled}
      />

      {!mobileOnly && (
        <Spin
          spinning={loadingRequests}
          tip="Cargando..."
          indicator={<LoadingOutlined />}
        >
          <Table
            bordered
            rowKey="id"
            className="table-ps employee-incidence-requests-table"
            columns={columns}
            dataSource={requests}
            data-cy="employee-incidence-requests-table"
            locale={{
              emptyText: renderIncidenceRequestsTableState(),
            }}
            scroll={{ x: 1000 }}
            pagination={{
              current: currentPage,
              onChange: onPaginationChange,
              pageSize: PAGE_SIZE,
              total: totalRequests,
              position: ['bottomCenter'],
              showSizeChanger: false,
              showTotal: (total, range) => `${range[1]} de ${total} registros`,
            }}
          />
        </Spin>
      )}

      {mobileOnly && (
        <Spin
          spinning={loadingRequests}
          tip="Cargando..."
          indicator={<LoadingOutlined />}
        >
          <>
            {incidenceRequestsTableState ? (
              <div className="mobile-incidence-list">
                {renderIncidenceRequestsTableState(true)}
              </div>
            ) : (
              <div className="mobile-incidence-list">
                {mobilePaginatedRequests.map((request) => {
                  const isExpanded = expandedMobileRequestId === request.id;
                  const showEditionActions = isEditableRequestStatus(
                    request.status
                  );

                  return (
                    <div className="mobile-incidence-item" key={request.id}>
                      <button
                        className="mobile-incidence-item-header"
                        type="button"
                        onClick={() => toggleMobileRequest(request.id)}
                        data-cy={`incidence-request-mobile-item-${request.id}`}
                      >
                        <div className="mobile-incidence-item-title">
                          <DownOutlined
                            className={`mobile-expand-icon ${isExpanded ? 'expanded' : ''}`}
                          />
                          <span className="mobile-incidence-type">
                            {request.incidenceType}
                          </span>
                        </div>
                        <Tag
                          className={`incidence-request-status incidence-request-status--${request.status}`}
                        >
                          {statusLabels[request.status]}
                        </Tag>
                      </button>

                      {isExpanded && (
                        <div className="mobile-incidence-item-body">
                          <div className="mobile-incidence-detail-row">
                            <span className="mobile-incidence-detail-label">
                              Folio
                            </span>
                            <span className="mobile-incidence-detail-value">
                              {request.folio}
                            </span>
                          </div>
                          <div className="mobile-incidence-detail-row">
                            <span className="mobile-incidence-detail-label">
                              Fecha inicial
                            </span>
                            <span className="mobile-incidence-detail-value">
                              {formatDisplayDate(request.startDate)}
                            </span>
                          </div>
                          <div className="mobile-incidence-detail-row">
                            <span className="mobile-incidence-detail-label">
                              Fecha final
                            </span>
                            <span className="mobile-incidence-detail-value">
                              {formatDisplayDate(request.endDate)}
                            </span>
                          </div>
                          <div className="mobile-incidence-detail-row">
                            <span className="mobile-incidence-detail-label">
                              Fecha solicitud
                            </span>
                            <span className="mobile-incidence-detail-value">
                              {formatDisplayDate(request.requestDate)}
                            </span>
                          </div>
                          <div className="mobile-incidence-detail-row">
                            <span className="mobile-incidence-detail-label">
                              Última actualización
                            </span>
                            <span className="mobile-incidence-detail-value">
                              {formatDisplayDate(request.updatedAt)}
                            </span>
                          </div>
                          <div className="mobile-incidence-item-actions">
                            <Button
                              className="mobile-action-btn"
                              icon={<EyeOutlined />}
                              type="text"
                              onClick={() => openDetailPage(request)}
                              data-cy={`incidence-request-view-btn-${request.id}`}
                            />
                            {showEditionActions && (
                              <Button
                                className="mobile-action-btn"
                                icon={<EditOutlined />}
                                type="text"
                                onClick={() =>
                                  openRequestDrawerForEdition(request)
                                }
                                data-cy={`incidence-request-edit-btn-${request.id}`}
                              />
                            )}
                            {showEditionActions && (
                              <Button
                                className="mobile-action-btn"
                                icon={
                                  <DeleteOutlined className="delete-icon" />
                                }
                                type="text"
                                onClick={() => openDeleteModal(request)}
                                data-cy={`incidence-request-delete-btn-${request.id}`}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!incidenceRequestsTableState && requests.length > 0 && (
              <div
                className="mobile-incidence-pagination"
                data-cy="incidence-request-mobile-pagination"
              >
                <div className="mobile-pagination-controls">
                  <Button
                    className="mobile-pagination-btn"
                    icon={<LeftOutlined />}
                    type="text"
                    onClick={() =>
                      setCurrentPage((previousPage) =>
                        Math.max(previousPage - 1, 1)
                      )
                    }
                    disabled={currentPage === 1}
                  />
                  <div className="mobile-pagination-numbers">
                    <Button className="active-page-btn" type="text">
                      {currentPage}
                    </Button>
                    {currentPage < totalPages && (
                      <Button
                        className="inactive-page-btn"
                        type="text"
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        {currentPage + 1}
                      </Button>
                    )}
                  </div>
                  <Button
                    className="mobile-pagination-btn"
                    icon={<RightOutlined />}
                    type="text"
                    onClick={() =>
                      setCurrentPage((previousPage) =>
                        Math.min(previousPage + 1, totalPages)
                      )
                    }
                    disabled={currentPage >= totalPages}
                  />
                </div>
                <p className="mobile-incidence-total">
                  {`${Math.min(currentPage * PAGE_SIZE, totalRequests)} de ${totalRequests} registros`}
                </p>
              </div>
            )}
          </>
        </Spin>
      )}

      <IncidenceRequestCreateDrawer
        open={requestDrawerOpen}
        mobileOnly={mobileOnly}
        requestToEdit={editingRequest}
        incidenceTypeOptions={incidenceTypeOptions}
        onSaveDraft={persistDraftRequest}
        onSubmit={persistAndSubmitRequest}
        onClose={closeRequestDrawer}
      />
      <IncidenceRequestDeleteConfirmModal
        open={deleteModalOpen}
        mobileOnly={mobileOnly}
        onClose={closeDeleteModal}
        onConfirm={() => void confirmDeleteRequest()}
      />

      {mobileOnly && (
        <div className="employee-incidence-mobile-cta">
          <Button
            type="primary"
            onClick={openRequestDrawer}
            block
            data-cy="incidence-request-open-drawer-btn"
          >
            Solicitar incidencia
          </Button>
        </div>
      )}
    </div>
  );
};
